import { useEffect, useState } from "react";
import { api } from "../lib/api";
import AppShell from "../components/AppShell";
import StatusBadge from "../components/StatusBadge";
import { toast } from "sonner";
import { Search, CheckCircle2, XCircle, Clock, AlertCircle, ShieldCheck } from "lucide-react";

/* ── KYC badge ─────────────────────────────────────────────────────── */
function KYCBadge({ status }) {
    const map = {
        approved:      { cls: "bg-emerald-100 text-emerald-700 border-emerald-200", icon: CheckCircle2  },
        pending:       { cls: "bg-blue-100 text-blue-700 border-blue-200",          icon: Clock         },
        rejected:      { cls: "bg-red-100 text-red-700 border-red-200",             icon: XCircle       },
        not_submitted: { cls: "bg-slate-100 text-slate-500 border-slate-200",       icon: AlertCircle   },
    };
    const { cls, icon: Icon } = map[status] || map.not_submitted;
    return (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-bold ${cls}`}>
            <Icon className="w-2.5 h-2.5" />
            {status === "not_submitted" ? "Not submitted" : status.charAt(0).toUpperCase() + status.slice(1)}
        </span>
    );
}

export default function AdminUsers() {
    const [users,   setUsers]   = useState([]);
    const [q,       setQ]       = useState("");
    const [loading, setLoading] = useState(false);
    const [kycing,  setKycing]  = useState({});   // uid → true while request in-flight

    const load = async (query = "") => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/users", { params: query ? { q: query } : {} });
            setUsers(data);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const updateStatus = async (id, status) => {
        try {
            await api.patch(`/admin/users/${id}`, { status });
            toast.success(`User ${status}`);
            load(q);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed");
        }
    };

    const updateKYC = async (id, action, reason = "") => {
        setKycing(p => ({ ...p, [id]: true }));
        try {
            const body = action === "reject" ? { action, reason: reason || "Rejected by admin" } : { action };
            await api.post(`/admin/kyc/user/${id}/action`, body);
            toast.success(action === "approve" ? "KYC approved ✓" : "KYC rejected");
            load(q);
        } catch (e) {
            toast.error(e.response?.data?.detail || "KYC update failed");
        } finally {
            setKycing(p => ({ ...p, [id]: false }));
        }
    };

    const handleReject = async (id) => {
        const reason = window.prompt("Rejection reason (required):");
        if (!reason) return;
        await updateKYC(id, "reject", reason);
    };

    return (
        <AppShell admin>
            <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
                <div>
                    <div className="overline">Admin</div>
                    <h1 className="font-display font-black text-3xl tracking-tighter text-slate-900 mt-2">Users</h1>
                    <p className="text-slate-500 text-sm mt-1">Manage member accounts, status, and KYC verification.</p>
                </div>
                <div className="flex gap-2">
                    <div className="relative">
                        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input
                            data-testid="admin-users-search"
                            value={q}
                            onChange={e => setQ(e.target.value)}
                            onKeyDown={e => e.key === "Enter" && load(q)}
                            placeholder="Search by email or name"
                            className="h-10 pl-9 pr-4 rounded-lg border border-slate-200 text-sm w-64 focus:outline-none focus:border-[#2563EB]"
                        />
                    </div>
                    <button onClick={() => load(q)} className="h-10 px-4 rounded-lg bg-[#1B2B6B] hover:bg-[#2563EB] text-white text-sm font-semibold transition-colors">
                        Search
                    </button>
                </div>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="bg-gradient-to-r from-[#1B2B6B] to-[#2A3D82] text-white">
                            <th className="text-left font-semibold py-3 px-4 text-xs uppercase tracking-wider">Name</th>
                            <th className="text-left font-semibold py-3 px-3 text-xs uppercase tracking-wider">Email</th>
                            <th className="text-left font-semibold py-3 px-3 text-xs uppercase tracking-wider">Code</th>
                            <th className="text-center font-semibold py-3 px-3 text-xs uppercase tracking-wider">Side</th>
                            <th className="text-center font-semibold py-3 px-3 text-xs uppercase tracking-wider">L / R</th>
                            <th className="text-center font-semibold py-3 px-3 text-xs uppercase tracking-wider">Status</th>
                            <th className="text-center font-semibold py-3 px-3 text-xs uppercase tracking-wider">KYC</th>
                            <th className="text-right font-semibold py-3 px-4 text-xs uppercase tracking-wider">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <tr><td colSpan={8} className="text-center py-8 text-slate-400">Loading…</td></tr>
                        )}
                        {!loading && users.length === 0 && (
                            <tr><td colSpan={8} className="text-center py-8 text-slate-400">No users found.</td></tr>
                        )}
                        {users.map(u => (
                            <tr key={u.id} className="border-b border-slate-50 hover:bg-slate-50 transition-colors" data-testid={`admin-user-row-${u.id}`}>
                                <td className="py-3 px-4 font-semibold text-slate-900">{u.full_name}</td>
                                <td className="py-3 px-3 text-slate-500 text-xs">{u.email}</td>
                                <td className="py-3 px-3 font-mono text-xs text-[#2563EB] font-bold">{u.referral_code}</td>
                                <td className="py-3 px-3 text-center">
                                    {u.side
                                        ? <span className={`font-bold ${u.side === "L" ? "wing-l" : "wing-r"}`}>{u.side === "L" ? "Left" : "Right"}</span>
                                        : <span className="text-slate-300">—</span>
                                    }
                                </td>
                                <td className="py-3 px-3 text-center font-mono text-xs">
                                    <span className="wing-l font-bold">{u.left_active_count}</span>
                                    <span className="text-slate-300 mx-1">/</span>
                                    <span className="wing-r font-bold">{u.right_active_count}</span>
                                </td>
                                <td className="py-3 px-3 text-center">
                                    <StatusBadge status={u.status} />
                                </td>
                                <td className="py-3 px-3 text-center">
                                    <KYCBadge status={u.kyc_status || "not_submitted"} />
                                </td>
                                <td className="py-3 px-4 text-right">
                                    <div className="inline-flex flex-wrap gap-1 justify-end">
                                        {/* Account status */}
                                        {u.status !== "active" && (
                                            <button onClick={() => updateStatus(u.id, "active")}
                                                className="px-2 py-1 text-xs font-semibold rounded bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200">
                                                Activate
                                            </button>
                                        )}
                                        {u.status !== "suspended" && u.role !== "admin" && (
                                            <button onClick={() => updateStatus(u.id, "suspended")}
                                                className="px-2 py-1 text-xs font-semibold rounded bg-red-50 text-red-700 hover:bg-red-100 border border-red-200">
                                                Suspend
                                            </button>
                                        )}

                                        {/* KYC actions */}
                                        {u.kyc_status !== "approved" && u.role === "member" && (
                                            <button
                                                disabled={kycing[u.id]}
                                                onClick={() => updateKYC(u.id, "approve")}
                                                className="px-2 py-1 text-xs font-semibold rounded bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200 disabled:opacity-50 flex items-center gap-1"
                                            >
                                                <ShieldCheck className="w-3 h-3" />
                                                {kycing[u.id] ? "…" : "Approve KYC"}
                                            </button>
                                        )}
                                        {u.kyc_status === "approved" && u.role === "member" && (
                                            <button
                                                disabled={kycing[u.id]}
                                                onClick={() => handleReject(u.id)}
                                                className="px-2 py-1 text-xs font-semibold rounded bg-blue-50 text-[#1B2B6B] hover:bg-blue-100 border border-blue-200 disabled:opacity-50"
                                            >
                                                Revoke KYC
                                            </button>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </AppShell>
    );
}
